<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" @cancel="closeModal()">
        <h1>{{ $t('pages.users.newUser.title') }}</h1>
      </modal-header>
    </template>

    <div class="mt-8">
      <form-card class="mt-7">
        <template #title>
          {{ $t('pages.users.newUser.sections.general.DEFAULT') }}
        </template>
        <template #description>
          {{ $t('pages.users.newUser.sections.general.description') }}
        </template>
        <div class="flex gap-x-5 w-full my-4">
          <Multiselect
            v-model="rawUser.salutation"
            class="w-1/2"
            :label="$t('user.salutation')"
            :placeholder="$t('user.salutation')"
            :options="getSalutations"
            :error="errors?.salutation"
            :can-deselect="false"
            :can-clear="false"
          />
          <Multiselect
            v-model="rawUser.language"
            class="w-1/2"
            :label="$t('user.language')"
            :placeholder="$t('user.language')"
            :options="getLanguages"
            :error="errors?.language"
            :can-deselect="false"
            :can-clear="false"
            :searchable="true"
          />
        </div>
        <FormInput
          v-model="rawUser.firstName"
          type="text"
          :label="$t('user.firstName')"
          :placeholder="$t('user.firstName')"
          :error="errors?.firstName"
          class="my-4"
        />
        <FormInput
          v-model="rawUser.lastName"
          type="text"
          :label="$t('user.lastName')"
          :placeholder="$t('user.lastName')"
          :error="errors?.lastName"
          class="my-4"
        />
        <FormInput
          v-model="rawUser.email"
          type="email"
          :label="$t('user.email')"
          :placeholder="$t('user.email')"
          :error="errors?.email"
          class="my-4"
        />
        <FormInput
          v-model="rawUser.phoneNumber"
          type="text"
          :label="$t('user.phoneNumber')"
          :placeholder="$t('user.phoneNumber')"
          :error="errors?.phoneNumber"
          class="my-4"
        />
      </form-card>
      <form-card v-if="canAssignRoles" class="mt-7">
        <template #title>
          {{ $t('pages.users.newUser.sections.roles.DEFAULT') }}
        </template>
        <template #description>
          {{ $t('pages.users.newUser.sections.roles.description') }}
        </template>
        <MultiselectTags
          v-model="rawUser.roles"
          :label="$t('user.role')"
          :options="getRoles"
          :object="false"
          :error="errors?.roles"
          class="my-4"
        />
      </form-card>
      <div class="w-full text-center">
        <custom-button variant="primary" class="mt-15" :busy="loading" @click="saveNewUser">
          {{ $t('save') }}
        </custom-button>
      </div>
    </div>
  </modal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import CustomButton from '@components/utils/CustomButton.vue';
import FormCard from '@components/utils/FormCard.vue';
import Multiselect from '@components/utils/Multiselect.vue';
import FormInput from '@components/utils/inputs/Input.vue';
import MultiselectTags from '@components/utils/MultiselectTags.vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { array, object, string } from 'yup';
import ModalHeader from '@components/utils/Modal/ModalHeader.vue';
import Modal from '@/components/utils/Modal/Modal.vue';
import { useConfirmPrompt } from '@utils/prompt/injects';
import { useNotify } from '@/components/utils/notifications';
import {FetchError, useFetchAction} from '@/hooks/fetch';
import { enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import { useStore } from '@/store';

const store = useStore();

const EMAIL_ALREADY_IN_USE_ERROR = 'email_already_in_use';

const isSuperAdmin = store.getters['auth/IS_SUPER_ADMIN'];
const canAssignRoles = computed(() =>
  (store.state.auth.user?.roles?.includes('ROLE_ADMIN') ?? false)
  || (store.state.auth.user?.roles?.includes('ROLE_SUPER_ADMIN') ?? false));

interface Props {
  open: boolean;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const notify = useNotify();

async function getRoles() {
  const roles = await enumOptionsProvider('/enums/NonDriverRole', (v) => ({
    label: t(`user.roles.${v}`),
    value: v,
  }))();
  if (isSuperAdmin) {
    return roles;
  }
  return roles.filter((el) => el.value !== 'ROLE_SUPER_ADMIN' && el.value !== 'ROLE_FINANCE_ADMIN');
}

const getLanguages = enumOptionsProvider('/enums/language', (v) => ({
  label: t(`language.${v}`),
  value: v,
}));

const getSalutations = enumOptionsProvider('/enums/salutation', (v) => ({
  label: t(`title.${v}`),
  value: v,
}));

const initialData = {
  roles: null,
  email: null,
  firstName: null,
  lastName: null,
  salutation: 'unknown',
  phoneNumber: null,
  language: 'en',
};

const rawUser = ref({ ...initialData });

const {
  data: user,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    roles: canAssignRoles.value ? array().nullable().required(t('validation.notEmpty')) : array().nullable(),
    email: string().nullable().required(t('validation.notEmpty')).email(t('validation.emailValid')),
    firstName: string().nullable().required(t('validation.notEmpty')),
    lastName: string().nullable().required(t('validation.notEmpty')),
    salutation: string().required(t('validation.notEmpty')),
    phoneNumber: string()
      .matches(/^\+[1-9]\d{1,14}$/, {
        excludeEmptyString: true,
        message: t('user.validation.phoneNumber'),
      }).nullable()
      .optional(),
    language: string().required(t('validation.notEmpty')),
  }),
  rawUser
);

function resetData() {
  rawUser.value = { ...initialData } as unknown as typeof rawUser['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const confirm = useConfirmPrompt();

const [exec, { loading }] = useFetchAction();

const saveNewUser = validatedFn(async () => {
  try {
    const answer = await confirm?.(t('pages.users.newUser.saveConfirmMessage'));
    if (!answer) return answer ?? false;

    await exec('/users', {
      method: 'POST',
      body: {
        email: user.value.email,
        firstName: user.value.firstName,
        lastName: user.value.lastName,
        salutation: user.value.salutation,
        phoneNumber: user.value.phoneNumber || null,
        language: user.value.language,
        ...(canAssignRoles.value ? { roles: user.value.roles } : {}),
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.users.newUser.saveSuccess'),
    });

    emit('saved');
    closeModal();
    return true;
  } catch (e) {
    if (e instanceof FetchError) {
      const errorCode = await e.response.json();
      if (errorCode?.error?.key === EMAIL_ALREADY_IN_USE_ERROR) {
        notify?.({
          variant: 'error',
          title: t('pages.users.newUser.emailUsedSaveError'),
        });
      } else {
        notify?.({
          variant: 'error',
          title: t('pages.users.newUser.saveError'),
        });
      }
    } else {
      notify?.({
        variant: 'error',
        title: t('pages.users.newUser.saveError'),
      });
    }

    return false;
  }
});
</script>

<style lang="scss" scoped></style>
